// Dropdown Menu - Reusable dropdown trigger + menu component
// Can render as a labeled button or an icon-only trigger.

import { createElement, clearElement } from '../utils/dom';

export interface DropdownMenuItem {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    /** Tooltip — when the item is disabled, say why. */
    title?: string;
}

export interface DropdownMenuConfig {
    /** Button label text. If provided, renders as a styled button. */
    label?: string;
    /** Icon element (e.g. from icons.gear()). If no label, renders icon-only. */
    icon?: SVGSVGElement;
    /** Tooltip */
    title?: string;
    /** Menu items (can be updated later via setItems) */
    items?: DropdownMenuItem[];
}

export interface DropdownMenu {
    /** The trigger — always a `<button type="button">`, labelled or icon-only. */
    element: HTMLElement;
    setItems(items: DropdownMenuItem[]): void;
}

export function createDropdownMenu(config: DropdownMenuConfig): DropdownMenu {
    let currentItems: DropdownMenuItem[] = config.items ?? [];

    const hasLabel = !!config.label;

    // Trigger element: labelled or icon-only, but ALWAYS a <button> — a div
    // trigger takes no focus, so the menu was unreachable from the keyboard.
    // `type="button"` keeps it out of any surrounding form's submit path.
    const trigger = createElement('button', {
        className: hasLabel ? 'cs-dropdown-trigger cs-dropdown-trigger--button' : 'cs-dropdown-trigger cs-dropdown-trigger--icon',
        attributes: { type: 'button', ...(config.title ? { title: config.title } : {}) },
    });

    if (config.icon) {
        trigger.appendChild(config.icon);
    }
    if (hasLabel) {
        const labelSpan = createElement('span', { textContent: config.label });
        trigger.appendChild(labelSpan);
    }

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        showMenu(e);
    });

    function showMenu(event: Event): void {
        // Remove any existing dropdown
        const existing = document.querySelector('.cs-dropdown-menu');
        if (existing) existing.remove();

        if (currentItems.length === 0) return;

        const menu = createElement('div', { className: 'cs-dropdown-menu' });

        for (const item of currentItems) {
            const menuItem = createElement('div', {
                className: item.disabled ? 'cs-menu-item cs-disabled' : 'cs-menu-item',
                textContent: item.label,
                attributes: item.title ? { title: item.title } : {},
                onClick: () => {
                    if (!item.disabled) {
                        menu.remove();
                        item.onClick();
                    }
                },
            });
            menu.appendChild(menuItem);
        }

        // Position below the trigger, aligned right
        const rect = trigger.getBoundingClientRect();
        menu.style.position = 'fixed';
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.right = `${window.innerWidth - rect.right}px`;

        document.body.appendChild(menu);

        // Close on outside click
        const closeHandler = (e: MouseEvent) => {
            if (!menu.contains(e.target as Node)) {
                menu.remove();
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }

    return {
        element: trigger,
        setItems(items: DropdownMenuItem[]) {
            currentItems = items;
        },
    };
}
